import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import orderTrackingService from '../services/orderTrackingService';
import errorHandler from '../utils/errorHandler';

const OrderTrackingContext = createContext();

export const useOrderTracking = () => {
    const context = useContext(OrderTrackingContext);
    if (!context) {
        throw new Error('useOrderTracking must be used within an OrderTrackingProvider');
    }
    return context;
};

export const OrderTrackingProvider = ({ children }) => {
    const [trackedOrders, setTrackedOrders] = useState(new Map());
    const [orderHistory, setOrderHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);

    // Initialize order tracking service
    useEffect(() => {
        initializeOrderTracking();
        
        // Cleanup on unmount
        return () => {
            orderTrackingService.cleanup();
        };
    }, []);

    const initializeOrderTracking = async () => {
        try {
            setLoading(true);
            const success = await orderTrackingService.initialize();
            setIsInitialized(success);
            
            if (success) {
                // Load order history
                await loadOrderHistory();
            }
        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Order tracking initialization' });
        } finally {
            setLoading(false);
        }
    };

    // Start tracking an order
    const startTracking = useCallback(async (orderId, callbacks = {}) => {
        try {
            if (!isInitialized) {
                await initializeOrderTracking();
            }

            const success = await orderTrackingService.startTracking(orderId, {
                ...callbacks,
                onStatusUpdate: (data) => {
                    setTrackedOrders(prev => new Map(prev.set(orderId, {
                        ...prev.get(orderId),
                        ...data,
                        lastUpdated: new Date()
                    })));
                    callbacks.onStatusUpdate?.(data);
                },
                onLocationUpdate: (data) => {
                    setTrackedOrders(prev => new Map(prev.set(orderId, {
                        ...prev.get(orderId),
                        location: data,
                        lastUpdated: new Date()
                    })));
                    callbacks.onLocationUpdate?.(data);
                },
                onDeliveryUpdate: (data) => {
                    setTrackedOrders(prev => new Map(prev.set(orderId, {
                        ...prev.get(orderId),
                        delivery: data,
                        lastUpdated: new Date()
                    })));
                    callbacks.onDeliveryUpdate?.(data);
                }
            });

            if (success) {
                // Get initial order status
                const orderStatus = await orderTrackingService.getOrderStatus(orderId);
                setTrackedOrders(prev => new Map(prev.set(orderId, {
                    ...orderStatus,
                    lastUpdated: new Date()
                })));
            }

            return success;
        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Start order tracking' });
            return false;
        }
    }, [isInitialized]);

    // Stop tracking an order
    const stopTracking = useCallback((orderId) => {
        try {
            orderTrackingService.stopTracking(orderId);
            setTrackedOrders(prev => {
                const newMap = new Map(prev);
                newMap.delete(orderId);
                return newMap;
            });
            return true;
        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Stop order tracking' });
            return false;
        }
    }, []);

    // Get tracked order
    const getTrackedOrder = useCallback((orderId) => {
        return trackedOrders.get(orderId);
    }, [trackedOrders]);

    // Get order details
    const getOrderDetails = useCallback(async (orderId) => {
        try {
            setLoading(true);
            const details = await orderTrackingService.getOrderDetails(orderId);
            return details;
        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Get order details' });
            throw error;
        } finally {
            setLoading(false);
        }
    }, []);

    // Get order timeline
    const getOrderTimeline = useCallback(async (orderId) => {
        try {
            const timeline = await orderTrackingService.getOrderTimeline(orderId);
            return timeline;
        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Get order timeline' });
            throw error;
        }
    }, []);

    // Get delivery location
    const getDeliveryLocation = useCallback(async (orderId) => {
        try {
            const location = await orderTrackingService.getDeliveryLocation(orderId);
            return location;
        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Get delivery location' });
            throw error;
        }
    }, []);

    // Update delivery location
    const updateDeliveryLocation = useCallback(async (orderId, location) => {
        try {
            setLoading(true);
            const updatedLocation = await orderTrackingService.updateDeliveryLocation(orderId, location);
            
            // Update tracked order
            setTrackedOrders(prev => new Map(prev.set(orderId, {
                ...prev.get(orderId),
                location: updatedLocation,
                lastUpdated: new Date()
            })));

            message.success('Delivery location updated successfully');
            return updatedLocation;
        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Update delivery location' });
            throw error;
        } finally {
            setLoading(false);
        }
    }, []);

    // Cancel order
    const cancelOrder = useCallback(async (orderId, reason = '') => {
        try {
            setLoading(true);
            const result = await orderTrackingService.cancelOrder(orderId, reason);
            
            // Update tracked order
            setTrackedOrders(prev => new Map(prev.set(orderId, {
                ...prev.get(orderId),
                status: 'cancelled',
                cancellation_reason: reason,
                lastUpdated: new Date()
            })));

            message.success('Order cancelled successfully');
            return result;
        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Cancel order' });
            throw error;
        } finally {
            setLoading(false);
        }
    }, []);

    // Reschedule order
    const rescheduleOrder = useCallback(async (orderId, newDateTime) => {
        try {
            setLoading(true);
            const result = await orderTrackingService.rescheduleOrder(orderId, newDateTime);
            
            // Update tracked order
            setTrackedOrders(prev => new Map(prev.set(orderId, {
                ...prev.get(orderId),
                scheduled_date_time: newDateTime,
                lastUpdated: new Date()
            })));

            message.success('Order rescheduled successfully');
            return result;
        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Reschedule order' });
            throw error;
        } finally {
            setLoading(false);
        }
    }, []);

    // Rate order
    const rateOrder = useCallback(async (orderId, rating, review = '') => {
        try {
            setLoading(true);
            const result = await orderTrackingService.rateOrder(orderId, rating, review);
            
            // Update tracked order
            setTrackedOrders(prev => new Map(prev.set(orderId, {
                ...prev.get(orderId),
                rating: rating,
                review: review,
                lastUpdated: new Date()
            })));

            message.success('Thank you for your rating!');
            return result;
        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Rate order' });
            throw error;
        } finally {
            setLoading(false);
        }
    }, []);

    // Load order history
    const loadOrderHistory = useCallback(async (page = 1, limit = 10, status = null) => {
        try {
            setLoading(true);
            const history = await orderTrackingService.getOrderHistory(page, limit, status);
            setOrderHistory(history);
            return history;
        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Load order history' });
            throw error;
        } finally {
            setLoading(false);
        }
    }, []);

    // Get order status display info
    const getStatusDisplayInfo = useCallback((status) => {
        return orderTrackingService.getStatusDisplayInfo(status);
    }, []);

    // Get estimated delivery time
    const getEstimatedDeliveryTime = useCallback((orderStatus) => {
        return orderTrackingService.getEstimatedDeliveryTime(orderStatus);
    }, []);

    // Get delivery progress
    const getDeliveryProgress = useCallback((orderStatus) => {
        return orderTrackingService.getDeliveryProgress(orderStatus);
    }, []);

    // Check if order can be cancelled
    const canCancelOrder = useCallback((orderStatus) => {
        return orderTrackingService.canCancelOrder(orderStatus);
    }, []);

    // Check if order can be rescheduled
    const canRescheduleOrder = useCallback((orderStatus) => {
        return orderTrackingService.canRescheduleOrder(orderStatus);
    }, []);

    // Check if order can be rated
    const canRateOrder = useCallback((orderStatus) => {
        return orderTrackingService.canRateOrder(orderStatus);
    }, []);

    // Get timeline events
    const getTimelineEvents = useCallback((timeline) => {
        return orderTrackingService.getTimelineEvents(timeline);
    }, []);

    // Get all tracked orders
    const getAllTrackedOrders = useCallback(() => {
        return Array.from(trackedOrders.values());
    }, [trackedOrders]);

    // Get orders by status
    const getOrdersByStatus = useCallback((status) => {
        return Array.from(trackedOrders.values()).filter(order => order.status === status);
    }, [trackedOrders]);

    // Get active orders (not delivered or cancelled)
    const getActiveOrders = useCallback(() => {
        const activeStatuses = ['pending', 'confirmed', 'preparing', 'ready', 'out_for_delivery'];
        return Array.from(trackedOrders.values()).filter(order => 
            activeStatuses.includes(order.status)
        );
    }, [trackedOrders]);

    const value = {
        // State
        trackedOrders: getAllTrackedOrders(),
        orderHistory,
        loading,
        isInitialized,
        
        // Actions
        startTracking,
        stopTracking,
        getTrackedOrder,
        getOrderDetails,
        getOrderTimeline,
        getDeliveryLocation,
        updateDeliveryLocation,
        cancelOrder,
        rescheduleOrder,
        rateOrder,
        loadOrderHistory,
        
        // Utilities
        getStatusDisplayInfo,
        getEstimatedDeliveryTime,
        getDeliveryProgress,
        canCancelOrder,
        canRescheduleOrder,
        canRateOrder,
        getTimelineEvents,
        getAllTrackedOrders,
        getOrdersByStatus,
        getActiveOrders
    };

    return (
        <OrderTrackingContext.Provider value={value}>
            {children}
        </OrderTrackingContext.Provider>
    );
};

export default OrderTrackingProvider;
